import { Box, CircularProgress, Typography } from "@material-ui/core";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import React, { useEffect, useState } from "react";
import { getCaseMandate } from "../service";

const COLORS = {
  green: "#27AE60",
  orange: "#F2994A",
  red: "#EB5757",
  closed: "#2F80ED",
};

// Calendar-accurate {months, days, hours, minutes} between now and a deadline.
function computeRemaining(deadlineIso) {
  const now = new Date();
  const end = new Date(deadlineIso);
  if (isNaN(end.getTime()) || end <= now) {
    return { expired: true, months: 0, days: 0, hours: 0, minutes: 0 };
  }
  let months =
    (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());
  let probe = new Date(now);
  probe.setMonth(probe.getMonth() + months);
  if (probe > end) {
    months -= 1;
    probe = new Date(now);
    probe.setMonth(probe.getMonth() + months);
  }
  let ms = end - probe;
  const days = Math.floor(ms / 86400000);
  ms -= days * 86400000;
  const hours = Math.floor(ms / 3600000);
  ms -= hours * 3600000;
  const minutes = Math.floor(ms / 60000);
  return { expired: false, months, days, hours, minutes };
}

function colorForDeadline(deadlineIso) {
  const daysLeft = (new Date(deadlineIso) - new Date()) / 86400000;
  if (daysLeft < 30) return COLORS.red; // includes past-due
  if (daysLeft < 90) return COLORS.orange;
  return COLORS.green;
}

/**
 * Arbitration mandate clock. Either pass a `mandate` object (from a list/detail
 * response) or a `disputeId` to fetch it. `compact` renders a single inline row.
 * `closureReason` is shown under the closed summary in the full view.
 */
export default function MandateClock({ mandate: mandateProp, disputeId, compact = false, closureReason }) {
  const [mandate, setMandate] = useState(mandateProp || null);
  const [loading, setLoading] = useState(!mandateProp && !!disputeId);
  const [, setTick] = useState(0); // forces a re-render each second

  useEffect(() => {
    if (mandateProp) {
      setMandate(mandateProp);
      return;
    }
    if (!disputeId) return;
    let active = true;
    setLoading(true);
    getCaseMandate(disputeId)
      .then((res) => {
        if (active) setMandate(res.data || null);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [disputeId, mandateProp]);

  // Tick every second so the countdown stays live.
  useEffect(() => {
    if (!mandate || mandate.is_closed) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [mandate]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: "0.5rem" }}>
        <CircularProgress size={14} />
        <Typography style={{ fontSize: "0.8rem", color: TEXT.muted }}>
          Loading mandate…
        </Typography>
      </Box>
    );
  }

  if (!mandate || (!mandate.deadline && !mandate.is_closed)) {
    return (
      <Typography style={{ fontSize: "0.8rem", color: TEXT.muted }}>
        Mandate not available.
      </Typography>
    );
  }

  // Closed case → show how long it took (compact only; full view shows "-").
  if (mandate.is_closed) {
    if (compact) {
      return (
        <Typography style={{ fontSize: "0.8rem", color: TEXT.muted }}>
          —
        </Typography>
      );
    }
    const within = mandate.within_mandate;
    return (
      <Box>
        <Typography style={{ fontSize: "0.8rem", color: TEXT.muted, fontWeight: 600 }}>
          Arbitration mandate
        </Typography>
        <Typography style={{ fontSize: "0.95rem", color: COLORS.closed, fontWeight: 600 }}>
          Disposal — took {mandate.time_taken}
        </Typography>
        {closureReason && (
          <Typography style={{ fontSize: "0.85rem", color: TEXT.secondary }}>
            Nature of Disposal: <strong>{closureReason}</strong>
          </Typography>
        )}
        {within !== undefined && (
          <Typography style={{ fontSize: "0.75rem", color: within ? COLORS.green : COLORS.red }}>
            {within ? "Within the 12-month mandate" : "Beyond the 12-month mandate"}
          </Typography>
        )}
      </Box>
    );
  }

  const r = computeRemaining(mandate.deadline);
  const color = colorForDeadline(mandate.deadline);
  const text = r.expired
    ? "Mandate expired"
    : `${r.months} months, ${r.days} days, ${r.hours} hours`;

  if (compact) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: "0.5rem" }}>
        <Typography style={{ fontSize: "0.85rem", color, fontWeight: 400 }}>
          {text}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      style={{
        border: `1px solid ${color}`,
        borderLeft: `5px solid ${color}`,
        borderRadius: 6,
        padding: "0.6rem 0.9rem",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography style={{ fontSize: "0.8rem", color: TEXT.muted, fontWeight: 600 }}>
        Arbitration mandate (12 months)
      </Typography>
      <Typography style={{ fontSize: "1.15rem", color, fontWeight: 600 }}>
        {text + " remaining"}
      </Typography>
      {!r.expired && (
        <Typography style={{ fontSize: "0.75rem", color: TEXT.muted }}>
          Deadline: {new Date(mandate.deadline).toLocaleDateString("en-GB")}
        </Typography>
      )}
    </Box>
  );
}
